import { useCallback, useRef } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

/** A single parsed SSE event. */
export interface SseEvent {
  /** Event type from the SSE `event:` field (empty string if none). */
  type: string;
  /** Parsed JSON payload from the SSE `data:` field. */
  data: Record<string, unknown>;
}

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * If no data (including ~20s heartbeats) arrives within this window,
 * the connection is considered dead.
 */
const READ_TIMEOUT_MS = 180_000; // 3 minutes

// ── Helpers (pure, no hooks) ─────────────────────────────────────────────────

/** Race reader.read() against a timeout. Rejects with SSE_STALE_CONNECTION on timeout. */
const readWithTimeout = (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number,
): Promise<ReadableStreamReadResult<Uint8Array>> => {
  let timerId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => {
      reader.cancel().catch(() => {});
      reject(new Error('SSE_STALE_CONNECTION'));
    }, timeoutMs);
  });
  return Promise.race([reader.read(), timeout]).finally(() => clearTimeout(timerId));
};

/** Parse a single SSE `data:` line into JSON, or null if not parseable. */
const parseDataLine = (line: string): Record<string, unknown> | null => {
  if (!line.startsWith('data:')) return null;
  const raw = line.substring(5).trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
};

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Low-level hook for reading Server-Sent Event streams.
 *
 * Handles:
 *  - SSE protocol parsing (event: / data: lines)
 *  - Stale connection detection (read timeout)
 *  - Clean abort
 *  - Tracking whether stream ended cleanly (received `done` event)
 *
 * Does NOT handle:
 *  - Message state, tool state, reconnection — those belong in useChat.
 *
 * The `onEvent` callback is stored in a ref, so identity changes never
 * affect the stream reader or cause dependency-chain cascades.
 */
export const useSseStream = (onEvent: (event: SseEvent) => void) => {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

  /** Abort the current stream (if any). Safe to call multiple times. */
  const abort = useCallback(() => {
    const reader = readerRef.current;
    if (reader) {
      readerRef.current = null;
      reader.cancel().catch(() => {});
    }
  }, []);

  /**
   * Read an SSE stream to completion.
   *
   * - Calls `onEvent` for every parsed SSE event (including `done`).
   * - Returns `true` if the stream ended cleanly (`done` event received).
   * - Returns `false` if the underlying stream closed without a `done` event.
   * - Throws `SSE_STALE_CONNECTION` on read timeout.
   * - Throws `SSE_SERVER_SHUTDOWN` if a `server_shutdown` event is received.
   *
   * Automatically aborts any previously-active stream before starting.
   */
  const readStream = useCallback(
    async (reader: ReadableStreamDefaultReader<Uint8Array>): Promise<boolean> => {
      // Abort any previous stream
      abort();
      readerRef.current = reader;

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      let receivedDone = false;

      try {
        while (true) {
          const { done, value } = await readWithTimeout(reader, READ_TIMEOUT_MS);
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            // Track event type
            if (line.startsWith('event:')) {
              currentEventType = line.substring(6).trim();
              continue;
            }

            const data = parseDataLine(line);
            if (!data) continue;

            const eventType = currentEventType;
            currentEventType = ''; // consume after use

            // Track clean completion
            if (eventType === 'done' || data.done === true) {
              receivedDone = true;
            }

            onEventRef.current({ type: eventType, data });
          }
        }
      } finally {
        // Only clear if this is still OUR reader
        if (readerRef.current === reader) {
          readerRef.current = null;
        }
      }

      return receivedDone;
    },
    [abort],
  );

  return { readStream, abort };
};
